import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Table from '../../shared/components/layout/Table';
import AddEditHolidays from '../components/forms/AddEditHolidays';
import Modal from '../../shared/components/ui/Modal';
import noHoliday from '../../shared/assets/svg/NoHoliday.svg';
import { useNavigate } from 'react-router-dom';
import {
  createHoliday,
  getAllHoliday,
  updateHoliday,
  addNonWorkingDay
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ConfigureHolidays = () => {
  const userId = Cookies.get('userId');
  const [isOpenAddEditModal, setIsOpenAddEditModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('');
  const { collapsed } = useSidebar();
  const [selectedHoliday, setSelectedHoliday] = useState();

  // Non-working days modal state
  const [isOpenNonWorkingModal, setIsOpenNonWorkingModal] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState(null); // single number 0..6
  const [isSavingNonWorking, setIsSavingNonWorking] = useState(false);

  const weekDays = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 }
  ];

  const selectWeekday = (dayValue) => {
    setSelectedWeekday(dayValue);
  };

  const { mutateAsync: addNonWorkingDayAsync } = useMutation({
    mutationFn: (payload) => addNonWorkingDay(payload)
  });

  const handleSaveNonWorkingDays = async () => {
    if (selectedWeekday === null || selectedWeekday === undefined) {
      toast.error('Please select a day');
      return;
    }
    try {
      setIsSavingNonWorking(true);
      await addNonWorkingDayAsync({ day: selectedWeekday });
      toast.success('Non-working day configured successfully');
      setIsOpenNonWorkingModal(false);
      setSelectedWeekday(null);
      refetchHolidays();
    } catch (err) {
      console.error('Error configuring non-working days', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to configure non-working days'
      );
    } finally {
      setIsSavingNonWorking(false);
    }
  };

  // Add tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  // Fetch vendors data with pagination
  const {
    data: allHolidays,
    refetch: refetchHolidays,
    isFetching
  } = useQuery({
    queryKey: ['getAllHoliday'],
    queryFn: () => getAllHoliday(),
    keepPreviousData: true
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Configure Holidays Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchHolidays();
  }, []);

  //stare the all vendors in the vendors state
  useEffect(() => {
    if (allHolidays?.data) {
      setHolidays(allHolidays?.data);
    }
  }, [allHolidays?.data]);

  const { mutate: handleCreateHoliday } = useMutation({
    mutationFn: (payload) => {
      if (modalType === 'Add') {
        return createHoliday(payload);
      } else if (modalType === 'Edit') {
        return updateHoliday(selectedHoliday.id, payload);
      }
      throw new Error('Invalid modal type');
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      setIsSubmitting(false);
      toast.success(`Holiday ${modalType === 'Add' ? 'created' : 'updated'} successfully`);
      refetchHolidays();
      setIsOpenAddEditModal(false);
      setModalType('');
      setSelectedHoliday();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error(`Error ${modalType === 'Add' ? 'creating' : 'updating'} holidays`, err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          `Error ${modalType === 'Add' ? 'creating' : 'updating'} holidays`
      );
    }
  });

  const handleAddHoliday = (data) => {
    const payload = modalType === 'Add' ? data : data.holidays?.[0];
    handleCreateHoliday(payload);
  };

  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    holidays && holidays?.length > 0 ? holidays?.slice(indexOfFirstItem, indexOfLastItem) : [];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const holidaysDataFormat = ['id', 'ocassion', 'start_date', 'end_date', 'noOfDays', 'actions'];

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const handleHolidayFormat = (item) => {
    return {
      id: item.id,
      ocassion: item?.ocassion,
      start_date: formatDate(item?.start_date),
      end_date: formatDate(item?.end_date),
      noOfDays: item?.noOfDays
    };
  };

  const transformedData = currentItems?.map((item) =>
    transformData(handleHolidayFormat(item), holidaysDataFormat)
  );

  const handleOnEdit = (id) => {
    const holiday = holidays.find((item) => item.id === Number(id));
    if (holiday) {
      setModalType('Edit');
      setSelectedHoliday(holiday);
      setIsOpenAddEditModal(true);
    }
  };

  return (
    <>
      {/* Non-Working Days Modal */}
      {isOpenNonWorkingModal && (
        <Modal
          isDirty={false}
          confirmOnClose={false}
          width={40}
          name={'Configure Non-Working Days'}
          onClose={() => setIsOpenNonWorkingModal(false)}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">Select the weekly off day:</p>
            <div className="grid grid-cols-2 gap-3">
              {weekDays.map((d) => (
                <label key={d.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="weekday"
                    checked={selectedWeekday === d.value}
                    onChange={() => selectWeekday(d.value)}
                  />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button displayType="secondary" onClick={() => setIsOpenNonWorkingModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveNonWorkingDays} disabled={isSavingNonWorking}>
                {isSavingNonWorking ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {isOpenAddEditModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name={` ${modalType === 'Add' ? 'Configure Holidays' : 'Edit Holiday'}`}
          onClose={() => setIsOpenAddEditModal(false)}>
          <AddEditHolidays
            isSubmitting={isSubmitting}
            onSubmit={handleAddHoliday}
            onClose={() => setIsOpenAddEditModal(false)}
            modalType={modalType}
            setModalType={setModalType}
            selectedHoliday={selectedHoliday}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Holidays' }} />
        </div>

        <GridWrapper width={100}>
          <div className="mt-[6rem] w-[100%] flex justify-end gap-2">
            <Button
              displayType="secondary"
              onClick={() => {
                setIsOpenNonWorkingModal(true);
              }}>
              Add Non Working day
            </Button>
            <Button
              onClick={() => {
                setModalType('Add');
                setIsOpenAddEditModal(true);
              }}>
              Configure Holidays
            </Button>
          </div>
          <div className="mt-[2rem] relative">
            {transformedData && transformedData.length > 0 ? (
              <div className="Editlevel-table-container h-[calc(100vh-12.5rem)] overflow-auto relative">
                <style>{`
                  .Editlevel-table-container .table-body {
                    position: static !important;
                    overflow-y: visible !important;
                    max-height: none !important;  
                    height: auto !important;
                    top: auto !important;
                    padding-bottom: 0 !important;
                  }
                  .Editlevel-table-container .table-container {
                    height: auto !important;
                  }
                  .Editlevel-table-container > div {
                    height: auto !important;  
                  }
                `}</style>
                <Table
                  headers={['Occassion', 'Start Date', 'End Date', 'No.of days', 'Actions']}
                  data={transformedData}
                  identifier={'id'}
                  onEdit={handleOnEdit}
                  //isViewEnable={true}
                  isEditEnable={true}
                  isDeleteEnable={false}
                  //   onDelete={""}
                  isWrap={true}
                />
                <PaginationComponent
                  page={currentPage}
                  totalPages={Math.ceil(holidays?.length / itemsPerPage)}
                  onPageChange={handlePageChange}
                  isFetching={isFetching}
                />
              </div>
            ) : (
              <div className="h-[100%] w-[100%] flex justify-center items-center">
                <img className="h-[40rem], w-[40rem]" src={noHoliday} alt="" />
              </div>
            )}
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default ConfigureHolidays;
